import { Metadata } from 'next';

export const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL || 'https://youraura.life';

/**
 * Generate canonical URL for a given path
 */
export function getCanonicalUrl(path: string): string {
    // Remove trailing slash and ensure leading slash
    const cleanPath = path === '/' ? '' : `/${path.replace(/^\/|\/$/g, '')}`;
    return `${BASE_URL}${cleanPath}`;
}

/**
 * Generate Open Graph image URL with fallback
 */
export function getOgImageUrl(imagePath?: string, title?: string): string {
    if (imagePath && imagePath.startsWith('http')) {
        return imagePath;
    }

    // If we have a title but no specific image path, generate a dynamic OG image
    if (!imagePath && title) {
        return `${BASE_URL}/api/og?title=${encodeURIComponent(title)}`;
    }

    if (!imagePath) {
        return `${BASE_URL}/og-image.jpg`;
    }

    // If it starts with /, it's a local path
    if (imagePath.startsWith('/')) {
        return `${BASE_URL}${imagePath}`;
    }

    // Otherwise, prepend BASE_URL
    return `${BASE_URL}/${imagePath}`;
}

/**
 * Generate consistent metadata for pages
 */
interface GenerateMetadataOptions {
    title: string;
    description: string;
    path: string;
    image?: string;
    type?: 'website' | 'article';
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    keywords?: string[];
    robots?: Metadata['robots'];
}

export function generatePageMetadata({
    title,
    description,
    path,
    image,
    type = 'website',
    publishedTime,
    modifiedTime,
    author,
    keywords,
    robots,
}: GenerateMetadataOptions): Metadata {
    const canonicalUrl = getCanonicalUrl(path);
    const ogImageUrl = getOgImageUrl(image, title);
    const pageTitle: Metadata['title'] = title.includes('YourAura')
        ? { absolute: title }
        : title;

    const metadata: Metadata = {
        title: pageTitle,
        description,
        keywords: keywords || [],
        alternates: {
            canonical: canonicalUrl,
        },
        openGraph: {
            type,
            locale: 'en_US',
            url: canonicalUrl,
            title,
            description,
            siteName: 'YourAura',
            images: [
                {
                    url: ogImageUrl,
                    width: 1200,
                    height: 630,
                    alt: title,
                },
            ],
        },
        twitter: {
            card: 'summary_large_image',
            title,
            description,
            images: [ogImageUrl],
        },
        robots,
    };

    // Add article-specific metadata
    if (type === 'article' && publishedTime) {
        metadata.openGraph = {
            ...metadata.openGraph,
            type: 'article',
            publishedTime,
            modifiedTime: modifiedTime || publishedTime,
            authors: author ? [author] : undefined,
        };
    }

    return metadata;
}

/**
 * Calculate word count from HTML content
 */
export function calculateWordCount(htmlContent: string): number {
    // Strip HTML tags and count words
    const textContent = htmlContent.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return textContent.split(' ').filter(word => word.length > 0).length;
}

/**
 * Strip HTML tags from content for plain text
 */
export function stripHtmlTags(htmlContent: string): string {
    return htmlContent.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Generate BlogPosting JSON-LD schema
 */
interface BlogPostSchemaOptions {
    title: string;
    description: string;
    image: string;
    datePublished: string;
    dateModified?: string;
    author: string;
    authorRole?: string;
    url: string;
    content?: string;
    imageAlt?: string;
    authorUrl?: string;
    articleSection?: string;
    keywords?: string;
    reviewedBy?: {
        name: string;
        url?: string;
        type?: 'Person' | 'Organization';
    };
}

export function generateBlogPostSchema({
    title,
    description,
    image,
    datePublished,
    dateModified,
    author,
    authorRole,
    url,
    content,
    imageAlt,
    authorUrl,
    articleSection,
    keywords,
    reviewedBy,
}: BlogPostSchemaOptions) {
    const wordCount = content ? calculateWordCount(content) : 0;
    const articleBody = content ? stripHtmlTags(content).substring(0, 5000) : undefined; // Limit to 5000 chars

    return {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: title,
        alternativeHeadline: description,
        image: {
            '@type': 'ImageObject',
            url: getOgImageUrl(image, title),
            caption: imageAlt || title,
        },
        datePublished,
        dateModified: dateModified || datePublished,
        author: {
            '@type': 'Person',
            name: author,
            jobTitle: authorRole,
            url: authorUrl || BASE_URL,
        },
        publisher: {
            '@type': 'Organization',
            name: 'YourAura',
            url: BASE_URL,
            logo: {
                '@type': 'ImageObject',
                url: `${BASE_URL}/logo.png`,
                width: 512,
                height: 512,
            },
        },
        description,
        url,
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': url,
        },
        inLanguage: 'en-US',
        isAccessibleForFree: true,
        educationalUse: 'Self-reflection and spiritual education',
        reviewedBy: reviewedBy
            ? {
                '@type': reviewedBy.type || 'Organization',
                name: reviewedBy.name,
                url: reviewedBy.url,
            }
            : undefined,
        wordCount: wordCount > 0 ? wordCount : undefined,
        articleBody: articleBody || undefined,
        articleSection: articleSection || 'Spiritual Wellness',
        keywords: keywords || 'astrology, numerology, palmistry, spiritual guidance, AI horoscope',
    };
}

/**
 * Generate FAQPage JSON-LD schema
 */
interface FAQItem {
    question: string;
    answer: string;
}

export function generateFAQSchema(faqs: FAQItem[]) {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
            },
        })),
    };
}

/**
 * Generate BreadcrumbList JSON-LD schema
 */
interface BreadcrumbItem {
    name: string;
    path: string;
}

export function generateBreadcrumbSchema(items: BreadcrumbItem[]) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: getCanonicalUrl(item.path),
        })),
    };
}

/**
 * Generate WebSite JSON-LD schema with search action
 */
export function generateWebsiteSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${BASE_URL}/#website`,
        url: BASE_URL,
        name: 'YourAura',
        alternateName: 'YourAura personal spiritual dashboard',
        description: 'Personal spiritual dashboard for daily aura, horoscope, Vedic charts, numerology, palm reading, tarot, dreams, compatibility, guidance, and saved readings',
        publisher: {
            '@id': `${BASE_URL}/#organization`,
        },
    };
}

/**
 * Generate Organization JSON-LD schema
 */
export function generateOrganizationSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${BASE_URL}/#organization`,
        name: 'YourAura',
        alternateName: ['Your Aura', 'youraura.life'],
        url: BASE_URL,
        logo: {
            '@type': 'ImageObject',
            url: `${BASE_URL}/logo.png`,
        },
        brand: {
            '@type': 'Brand',
            name: 'YourAura',
            alternateName: 'Your Aura',
        },
        owns: {
            '@type': 'SoftwareApplication',
            name: 'Aarika',
            alternateName: 'YourAura AI reading assistant',
            applicationCategory: 'LifestyleApplication',
            operatingSystem: 'Web',
            url: BASE_URL,
        },
        sameAs: [
            'https://twitter.com/your_aura_life',
            'https://www.instagram.com/youraura.life/',
            'https://www.facebook.com/youraura.life',
        ],
        contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'Customer Support',
            email: 'support@youraura.life',
            availableLanguage: 'English',
        },
    };
}

export function generateSoftwareApplicationSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        '@id': `${BASE_URL}/#software-application`,
        name: 'YourAura',
        alternateName: 'Aarika',
        applicationCategory: 'LifestyleApplication',
        applicationSubCategory: 'AI astrology, numerology, and palm reading dashboard',
        operatingSystem: 'Web',
        url: BASE_URL,
        publisher: {
            '@id': `${BASE_URL}/#organization`,
        },
        provider: {
            '@id': `${BASE_URL}/#organization`,
        },
        offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'INR',
            description: 'Free account access with optional paid plans for deeper features.',
        },
        featureList: [
            'Personalized daily horoscope',
            'Birth chart reading',
            'Life path numerology calculator',
            'Signup-gated AI palm reading',
            'Daily spiritual guidance dashboard',
        ],
    };
}
/**
 * Generate CollectionPage JSON-LD schema
 */
export function generateCollectionPageSchema(name: string, description: string, url: string, items: { slug: string; title: string }[]) {
    return {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name,
        description,
        url,
        mainEntity: {
            '@type': 'ItemList',
            numberOfItems: items.length,
            itemListElement: items.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                url: `${BASE_URL}/blog/${item.slug}`,
                name: item.title,
            })),
        },
    };
}

interface PersonSchemaOptions {
    name: string;
    description: string;
    slug: string;
    jobTitle?: string;
    knowsAbout?: string[];
}

export function generatePersonSchema({ name, description, slug, jobTitle, knowsAbout }: PersonSchemaOptions) {
    return {
        '@context': 'https://schema.org',
        '@type': 'Person',
        '@id': `${BASE_URL}/blog/authors/${slug}#person`,
        name,
        description,
        url: `${BASE_URL}/blog/authors/${slug}`,
        jobTitle,
        worksFor: {
            '@id': `${BASE_URL}/#organization`,
        },
        knowsAbout,
    };
}
